import { useSelector, shallowEqual } from "react-redux";
import { useMemo } from "react";

/**
 * Custom hook to get current user permissions and helpers
 *
 * Usage:
 * const { permissions, hasPermission, hasAnyPermission, hasAllPermissions } = usePermissions();
 * if (hasPermission('hrms.leave.reset')) { ... }
 * if (hasAnyPermission(['perm1', 'perm2'])) { ... }
 * if (hasAllPermissions(['perm1', 'perm2'])) { ... }
 */
export const usePermissions = () => {
    // Select permissions array from redux store with shallowEqual for perf
    const permissions = useSelector(
        (store) => store.auth.user?.permissions || [],
        shallowEqual
    );

    // Single permission check
    const hasPermission = useMemo(() => {
        return (perm) => permissions.includes(perm);
    }, [permissions]);

    // Check if user has at least one of the given permissions
    const hasAnyPermission = useMemo(() => {
        return (perms) => perms.some((perm) => permissions.includes(perm));
    }, [permissions]);

    // Check if user has all the given permissions
    const hasAllPermissions = useMemo(() => {
        return (perms) => perms.every((perm) => permissions.includes(perm));
    }, [permissions]);

    return { permissions, hasPermission, hasAnyPermission, hasAllPermissions };
};
